/**
 * NuUserPanel — the fixed right-side user panel: gateway-style collapsible
 * slider, 60px icon strip ↔ 340px expanded (user card + role display, compact
 * metric tiles, plan/credits, notifications, tool buttons, theme toggle,
 * sign-out).
 *
 * Ported from NU-DATA-DASHBOARD/kits/nu-data/components/right-rail-slider.jsx
 * (NuDataRightRail). Differences, all deliberate:
 *  - Sign-out is wired to the REAL auth seam: `onLogout` (useAuth().logout →
 *    tokenStore clear) then navigation to /login — not the kit's bare
 *    `nav('#/login')`.
 *  - Theme toggle calls the app's existing ThemeContext mechanism via
 *    `onToggleTheme` (data-theme + .dark stamped in lockstep, S1-1).
 *  - The kit's role/tenant SWITCHER block rendered only when its demo
 *    `window.NuRBAC` + `onSetRole` were present; neither exists here, so the
 *    kit itself would not render it — omitted, role DISPLAY retained.
 *  - Metrics / credits / notifications remain the kit's static demo payload
 *    (visual fidelity against user-panel-expanded.png); live data is a
 *    later-wave wiring task.
 * Expanded state persists in localStorage 'nu-data-right-open' (kit key).
 */

import React, { useEffect, useState } from "react";
import { NuIcons } from "../shared/icons";
import type { ShellTheme } from "./NuShellRail";
import type { NavigateFn } from "./navConfig";

const RS_NOTIFS = [
  {
    id: 1,
    title: "Uploading list (Q2 outreach)",
    body: "Validation in progress",
    when: "Just now",
    read: false,
  },
  {
    id: 2,
    title: "Credit request approved",
    body: "Current credits: 26,743,506",
    when: "6h ago",
    read: false,
  },
  {
    id: 3,
    title: "Running low on credits",
    body: "Left: 5,121",
    when: "Yesterday",
    read: true,
  },
] as const;

const RS_METRICS = [
  { label: "Lookups · today", value: "1,842", icon: "search", tone: "success" },
  { label: "Hit-rate", value: "98.8%", icon: "tick", tone: "success" },
  { label: "Avg latency", value: "64ms", icon: "refresh", tone: "primary" },
  { label: "Active lists", value: "7", icon: "list", tone: "muted" },
] as const;

const RS_CREDITS = { used: 49872, total: 100000 } as const;

function RsThemeIcon({ theme }: { theme: ShellTheme }) {
  return theme === "dark" ? (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ) : (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function RsCreditRing({ pct, size = 30 }: { pct: number; size?: number }) {
  const r = (size - 5) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: "rotate(-90deg)" }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--nu-border-strong)"
        strokeWidth="3"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--nu-primary)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
      />
    </svg>
  );
}

const signOutSvg = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export interface ShellUser {
  name: string;
  email?: string;
  /** Raw session role string, displayed uppercase in the user card. */
  role?: string;
}

export interface NuUserPanelProps {
  theme: ShellTheme;
  onToggleTheme?: () => void;
  onNavigate: NavigateFn;
  /** Real sign-out: clears the session via the tokenStore seam. The panel
   *  navigates to /login after it resolves. */
  onLogout?: () => void | Promise<void>;
  user?: ShellUser;
  onOpenSupport?: () => void;
}

export function NuUserPanel({
  theme,
  onToggleTheme,
  onNavigate,
  onLogout,
  user,
  onOpenSupport,
}: NuUserPanelProps) {
  const displayUser: ShellUser = user ?? {
    name: "NU DATA User",
    role: "member",
  };
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem("nu-data-right-open") === "1";
    } catch {
      return false;
    }
  });
  function toggle() {
    setOpen((v) => {
      const n = !v;
      try {
        localStorage.setItem("nu-data-right-open", n ? "1" : "0");
      } catch {
        /* storage unavailable */
      }
      return n;
    });
  }
  useEffect(() => {
    document.documentElement.style.setProperty("--nu-right-gutter", "60px");
    document.body.setAttribute("data-nu-right-slider", "1");
    return () => document.body.removeAttribute("data-nu-right-slider");
  }, []);

  const width = open ? 340 : 60;
  const initials = displayUser.name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const creditPct = RS_CREDITS.used / RS_CREDITS.total;
  const unread = RS_NOTIFS.filter((n) => !n.read).length;
  const nav = (r: string) => onNavigate(r);

  const tools = [
    {
      id: "profile",
      label: "My profile",
      icon: "user",
      onClick: () => nav("/profile"),
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: "bell",
      badge: unread,
      onClick: () => nav("/help"),
    },
    {
      id: "support",
      label: "Support",
      icon: "chat",
      onClick: () => onOpenSupport?.(),
    },
    {
      id: "help",
      label: "Help center",
      icon: "help",
      onClick: () => nav("/help"),
    },
    {
      id: "settings",
      label: "Settings",
      icon: "settings",
      onClick: () => nav("/settings"),
    },
  ] as const;

  async function signOut() {
    await onLogout?.();
    nav("/login");
  }

  return (
    <aside
      aria-label="User panel"
      data-open={open ? "true" : "false"}
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width,
        zIndex: 60,
        background: "var(--nu-surface)",
        borderLeft: "1px solid var(--nu-border-strong)",
        boxShadow: open
          ? "-16px 0 40px -20px rgba(0,0,0,0.4)"
          : "-2px 0 8px -6px rgba(0,0,0,0.25)",
        transition: "width 220ms cubic-bezier(.16,1,.3,1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Toggle handle */}
      <button
        onClick={toggle}
        aria-label={open ? "Collapse panel" : "Expand panel"}
        aria-expanded={open ? "true" : "false"}
        style={{
          position: "absolute",
          top: 64,
          left: -13,
          width: 26,
          height: 26,
          borderRadius: 999,
          background: "var(--nu-surface)",
          border: "1px solid var(--nu-border-strong)",
          boxShadow: "0 4px 12px -4px rgba(0,0,0,0.35)",
          color: "var(--nu-text-muted)",
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          zIndex: 61,
        }}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: open ? "rotate(0)" : "rotate(180deg)",
            transition: "transform 220ms",
          }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {/* User (top) — identity + profile actions */}
      <div
        style={{
          padding: open ? "20px 16px 14px" : "14px 8px",
          borderBottom: "1px solid var(--nu-border)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          justifyContent: open ? "flex-start" : "center",
        }}
      >
        <button
          onClick={() => {
            if (!open) toggle();
            else nav("/profile");
          }}
          aria-label={open ? "View profile" : "Open user panel"}
          title={open ? "View profile" : displayUser.name}
          style={{
            border: "none",
            padding: 0,
            background: "transparent",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: 38,
              height: 38,
              display: "grid",
              placeItems: "center",
              borderRadius: 999,
              background:
                "linear-gradient(135deg, var(--nu-brand-300), var(--nu-primary))",
              color: "var(--nu-on-primary)",
              font: "700 13px/1 var(--nu-font-sans)",
              boxShadow: "var(--nu-shadow-brand)",
            }}
          >
            {initials}
          </span>
        </button>
        {open && (
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              className="nu-h4"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Hi, {displayUser.name.split(" ")[0]}
            </div>
            {displayUser.role && (
              <div
                className="nu-tiny nu-subtle"
                data-testid="user-panel-role"
                style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
              >
                {displayUser.role}
              </div>
            )}
            {displayUser.email && (
              <div
                className="nu-tiny nu-muted"
                style={{
                  marginTop: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayUser.email}
              </div>
            )}
          </div>
        )}
        {open && (
          <button
            onClick={signOut}
            aria-label="Sign out"
            title="Sign out"
            style={{
              width: 30,
              height: 30,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              border: "none",
              background: "transparent",
              color: "var(--nu-text-subtle)",
              cursor: "pointer",
              borderRadius: "var(--nu-radius-control)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--nu-danger)";
              e.currentTarget.style.background =
                "color-mix(in srgb, var(--nu-danger) 10%, transparent)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--nu-text-subtle)";
              e.currentTarget.style.background = "transparent";
            }}
          >
            {signOutSvg}
          </button>
        )}
      </div>

      {/* Collapsed: icon tools. Expanded: scrollable detail. */}
      {!open ? (
        <nav
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "12px 0",
            gap: 6,
            flex: 1,
          }}
        >
          {tools.map((t) => {
            const Icon = NuIcons[t.icon];
            const badge = "badge" in t ? t.badge : 0;
            return (
              <button
                key={t.id}
                onClick={t.onClick}
                aria-label={t.label}
                title={t.label}
                style={{
                  position: "relative",
                  width: 40,
                  height: 40,
                  display: "grid",
                  placeItems: "center",
                  border: "none",
                  background: "transparent",
                  color: "var(--nu-text-muted)",
                  cursor: "pointer",
                  borderRadius: "var(--nu-radius-control)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--nu-surface-alt)";
                  e.currentTarget.style.color = "var(--nu-text)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "var(--nu-text-muted)";
                }}
              >
                <Icon size={18} />
                {badge ? (
                  <span
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      minWidth: 15,
                      height: 15,
                      padding: "0 3px",
                      borderRadius: 999,
                      background: "var(--nu-danger)",
                      color: "#fff",
                      font: "700 9px/15px var(--nu-font-mono)",
                      textAlign: "center",
                    }}
                  >
                    {badge}
                  </span>
                ) : null}
              </button>
            );
          })}
          <div title="Credits" style={{ marginTop: 4 }}>
            <RsCreditRing pct={creditPct} />
          </div>
          <span style={{ flex: 1 }} />
          <button
            onClick={onToggleTheme}
            aria-label="Toggle theme"
            title="Toggle theme"
            style={{
              width: 40,
              height: 40,
              display: "grid",
              placeItems: "center",
              border: "none",
              background: "transparent",
              color: "var(--nu-text-muted)",
              cursor: "pointer",
              borderRadius: "var(--nu-radius-control)",
            }}
          >
            <RsThemeIcon theme={theme} />
          </button>
        </nav>
      ) : (
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {/* Compact metrics */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            {RS_METRICS.map((m) => {
              const Icon = NuIcons[m.icon];
              const col =
                m.tone === "success"
                  ? "var(--nu-success)"
                  : m.tone === "primary"
                    ? "var(--nu-primary)"
                    : "var(--nu-text-muted)";
              return (
                <div
                  key={m.label}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "var(--nu-radius-card)",
                    background: "var(--nu-surface-alt)",
                    border: "1px solid var(--nu-border)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: col,
                    }}
                  >
                    <Icon size={13} />
                    <span
                      className="nu-tiny nu-subtle"
                      style={{
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      {m.label}
                    </span>
                  </div>
                  <div
                    style={{
                      font: "600 18px/1.1 var(--nu-font-sans)",
                      color: "var(--nu-text)",
                      marginTop: 4,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {m.value}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Subscription / credits */}
          <div
            style={{
              padding: "12px 14px",
              borderRadius: "var(--nu-radius-card)",
              background: "var(--nu-bg)",
              border: "1px solid var(--nu-border-strong)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                className="nu-tiny nu-subtle"
                style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
              >
                Plan Active · Pro
              </span>
              <button
                className="nu-btn nu-btn-text"
                style={{ padding: 0, height: "auto" }}
                onClick={() => nav("/profile")}
              >
                Manage
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 4,
                marginTop: 4,
              }}
            >
              <span
                style={{
                  font: "500 22px/1 var(--nu-font-sans)",
                  color: "var(--nu-text)",
                }}
              >
                {RS_CREDITS.used.toLocaleString()}
              </span>
              <span className="nu-small nu-subtle">
                / {RS_CREDITS.total.toLocaleString()} credits
              </span>
            </div>
            <div
              className="nu-progress-track"
              style={{ marginTop: 8, background: "var(--nu-surface)" }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${creditPct * 100}%`,
                  background: "var(--nu-primary)",
                  borderRadius: 999,
                }}
              />
            </div>
            <button
              className="nu-btn nu-btn-outline"
              style={{ width: "100%", marginTop: 10, height: 32 }}
            >
              <NuIcons.plus size={13} /> Request more credits
            </button>
          </div>

          {/* Notifications */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 8,
              }}
            >
              <h4 className="nu-h4">
                Notifications{" "}
                {unread > 0 && (
                  <span
                    className="nu-pill nu-pill-primary"
                    style={{ fontSize: 9, marginLeft: 4 }}
                  >
                    {unread} new
                  </span>
                )}
              </h4>
              <button
                className="nu-btn nu-btn-text"
                style={{ padding: 0, height: "auto" }}
              >
                Mark all
              </button>
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {RS_NOTIFS.map((n) => (
                <li
                  key={n.id}
                  style={{
                    padding: "9px 11px",
                    borderRadius: "var(--nu-radius-card)",
                    background: n.read ? "transparent" : "var(--nu-brand-50)",
                    border: `1px solid ${n.read ? "var(--nu-border)" : "var(--nu-brand-200)"}`,
                    position: "relative",
                  }}
                >
                  <p
                    className="nu-small"
                    style={{ fontWeight: 500, color: "var(--nu-text)" }}
                  >
                    {n.title}
                  </p>
                  {n.body && (
                    <p className="nu-tiny nu-muted" style={{ marginTop: 2 }}>
                      {n.body}
                    </p>
                  )}
                  <p className="nu-tiny nu-subtle" style={{ marginTop: 5 }}>
                    {n.when}
                  </p>
                  {!n.read && (
                    <span
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: "var(--nu-primary)",
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Tool buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              borderTop: "1px solid var(--nu-border)",
              paddingTop: 10,
            }}
          >
            {tools.map((t) => {
              const Icon = NuIcons[t.icon];
              const badge = "badge" in t ? t.badge : 0;
              return (
                <button
                  key={t.id}
                  onClick={t.onClick}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "9px 10px",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "var(--nu-text-muted)",
                    font: "500 13px/1 var(--nu-font-sans)",
                    borderRadius: "var(--nu-radius-control)",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--nu-surface-alt)";
                    e.currentTarget.style.color = "var(--nu-text)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "var(--nu-text-muted)";
                  }}
                >
                  <span
                    style={{ position: "relative", display: "inline-flex" }}
                  >
                    <Icon size={17} />
                    {badge ? (
                      <span
                        style={{
                          position: "absolute",
                          top: -5,
                          right: -7,
                          minWidth: 14,
                          height: 14,
                          padding: "0 3px",
                          borderRadius: 999,
                          background: "var(--nu-danger)",
                          color: "#fff",
                          font: "700 8.5px/14px var(--nu-font-mono)",
                          textAlign: "center",
                        }}
                      >
                        {badge}
                      </span>
                    ) : null}
                  </span>
                  {t.label}
                </button>
              );
            })}
            <button
              onClick={onToggleTheme}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "9px 10px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "var(--nu-text-muted)",
                font: "500 13px/1 var(--nu-font-sans)",
                borderRadius: "var(--nu-radius-control)",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--nu-surface-alt)";
                e.currentTarget.style.color = "var(--nu-text)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--nu-text-muted)";
              }}
            >
              <RsThemeIcon theme={theme} />{" "}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
            <button
              onClick={signOut}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "9px 10px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "var(--nu-danger)",
                font: "500 13px/1 var(--nu-font-sans)",
                borderRadius: "var(--nu-radius-control)",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "color-mix(in srgb, var(--nu-danger) 10%, transparent)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              {signOutSvg}
              Sign out
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}

export default NuUserPanel;
